import { type ReactNode, useMemo, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { Instructor } from "@/common/instructor/types";
import { MIN_ENROLLMENT_COUNT_FOR_DISPLAY } from "@/components/CourseContent/CourseRunSelector/constants";
import { maxWidthContainer } from "@/components/CourseContent/styles/constants";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import InstructorCard from "./InstructorCard";
import AboutCertificate from "./AboutCertificate";
import EnrollmentCard from "./EnrollmentCard";
import { IconTypes } from "../icons/types";
import useTranslations from "@/i18n/useTranslations";

export type HighlightItem = {
  iconName: IconTypes;
  title: string;
  subtitle: string;
  showPartnerName?: boolean;
  partnerName?: string;
};

type WhyLearnersChooseProps = {
  instructor?: Instructor | null;
  highlights: HighlightItem[];
  partnerName: string;
  location: string;
  enrollmentCount?: number;
  isArchived?: boolean;
  isEnrollable?: boolean;
  children?: ReactNode;
};

export default function WhyLearnersChoose({
  instructor,
  highlights,
  partnerName,
  location,
  enrollmentCount,
  isArchived = false,
  isEnrollable = false,
  children,
}: WhyLearnersChooseProps) {
  const t = useTranslations("Course");

  const hasViewedRef = useRef(false);
  const webElement = useMemo(
    () => ({
      elementType: "SECTION",
      htmlId: "why-learners-choose-section",
      location,
      position: "main-content",
      name: "why-learners-choose-section",
      text: "Why learners choose this course",
    }),
    [location],
  );
  const { elementViewed } = useElementEvents({ webElement });
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  const showEnrollmentCount =
    !isArchived &&
    isEnrollable &&
    enrollmentCount != null &&
    enrollmentCount >= MIN_ENROLLMENT_COUNT_FOR_DISPLAY;

  return (
    <div
      ref={inViewRef}
      className="bg-putty-300"
      style={{
        maxWidth: "none",
        width: "100vw",
        marginLeft: "calc(50% - 50vw)",
      }}
    >
      <div
        className={`${maxWidthContainer} w-full flex flex-col py-6 px-4 md:py-16`}
      >
        <h2 className="text-left font-bold text-2xl md:text-3xl text-elm-500 mt-0 mb-2">
          {t("whyLearnersChooseThisCourse")}
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {instructor && (
            <div className="flex flex-col h-full">
              <h4 className="text-lg md:text-xl text-elm-500">
                {t("meetYourInstructors")}
              </h4>
              <InstructorCard instructor={instructor} />
            </div>
          )}

          <div
            className={`flex flex-col h-full ${!instructor ? "md:col-span-2" : ""}`}
          >
            <h4 className="text-lg md:text-xl text-elm-500">
              {t("aboutTheCertificate")}
            </h4>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 h-full">
              <AboutCertificate
                highlights={highlights}
                partnerName={partnerName}
              />

              {showEnrollmentCount && enrollmentCount != null && (
                <div className="col-span-1 self-end md:col-span-2">
                  <EnrollmentCard enrollmentCount={enrollmentCount} />
                </div>
              )}
            </div>
          </div>
        </div>

        {children && <div className="mt-16">{children}</div>}
      </div>
    </div>
  );
}
